import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTutorial } from '../../hooks/useTutorial';
import { useTutorialNavigation } from '../../hooks/useTutorialNavigation';
import { useTutorialCompletion } from '../../hooks/useTutorialCompletion';
import { useAuth } from '../../hooks/useAuth';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import Breadcrumb from '../../components/Breadcrumb';
import LoginPromptModal from '../../components/modals/LoginPromptModal';
import { generateTutorialBreadcrumb } from '../../utils/breadcrumb';
import { Loader2, ArrowLeft, Clock, Award, PlayCircle, ChevronLeft, ChevronRight, CheckCircle, Circle } from 'lucide-react';

interface TutorialPageProps {
  slug?: string;
}

export default function TutorialPage({ slug: propSlug }: TutorialPageProps) {
  const { slug: paramSlug, potentialSlug } = useParams<{ slug?: string; potentialSlug?: string }>();
  const tutorialSlug = propSlug || paramSlug || potentialSlug;
  const { tutorial, loading, error } = useTutorial(tutorialSlug);
  const { navigation, loading: navLoading } = useTutorialNavigation(
    tutorial?.id,
    tutorial?.category_slug,
    tutorial?.order
  );
  const { user } = useAuth();
  const { isCompleted, loading: completionLoading, toggleComplete } = useTutorialCompletion(tutorial?.id);
  const { setItems } = useBreadcrumb();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  React.useEffect(() => {
    if (tutorial) {
      setItems(generateTutorialBreadcrumb(tutorial));
    }
  }, [tutorial, setItems]);

  const handleToggleComplete = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    try {
      setActionLoading(true);
      await toggleComplete();
    } catch (err) {
      console.error('Error toggling completion:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !tutorial) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        <div className="text-red-600 bg-red-50 p-4 rounded-lg">
          {error || 'Tutorial not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{tutorial.estimated_time}</span>
              </div>
              <div className="flex items-center gap-1">
                <Award className="h-4 w-4" />
                <span className="capitalize">{tutorial.difficulty_level}</span>
              </div>
            </div>
            
            <h1 className="text-3xl font-bold mb-4">{tutorial.title}</h1>
            <p className="text-gray-600 text-lg">{tutorial.description}</p>
          </div>

          {/* Video Section */}
          {tutorial.video_url && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <PlayCircle className="h-6 w-6 text-primary-500" />
                <h2 className="text-xl font-semibold">Video Tutorial</h2>
              </div>
              
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                {tutorial.video_url.includes('youtube.com') || tutorial.video_url.includes('youtu.be') ? (
                  <iframe
                    src={tutorial.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title={tutorial.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : tutorial.video_url.includes('vimeo.com') ? (
                  <iframe
                    src={tutorial.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    title={tutorial.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    controls
                    className="w-full h-full"
                    poster={tutorial.image_url}
                  >
                    <source src={tutorial.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>
          )}

          {/* Written Content */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold mb-6">Step-by-Step Guide</h2>
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: tutorial.written_content }}
            />
          </div>

          {/* Completion Button */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <button
              onClick={handleToggleComplete}
              disabled={completionLoading || actionLoading}
              className={`w-full flex items-center justify-center gap-3 py-4 px-6 rounded-lg font-medium transition-all ${
                isCompleted
                  ? 'bg-green-50 text-green-700 border-2 border-green-500 hover:bg-green-100'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionLoading || completionLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : isCompleted ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Completed</span>
                </>
              ) : (
                <>
                  <Circle className="h-5 w-5" />
                  <span>Mark as Complete</span>
                </>
              )}
            </button>
            {!user && (
              <p className="text-center text-sm text-gray-500 mt-3">
                Sign in to track your progress
              </p>
            )}
          </div>

          {/* Navigation */}
          <div className="space-y-6">
            {/* Tutorial Navigation */}
            {!navLoading && tutorial?.category_slug && (navigation.previous || navigation.next) && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Continue Learning</h3>
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    {navigation.previous ? (
                      <Link
                        to={`/tutorials/${navigation.previous.slug}`}
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium group"
                      >
                        <ChevronLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
                        <div className="text-left">
                          <div className="text-sm text-gray-500">Previous Tutorial</div>
                          <div className="font-medium">{navigation.previous.title}</div>
                        </div>
                      </Link>
                    ) : (
                      <div></div>
                    )}
                  </div>
                  
                  <div className="flex-1 text-right">
                    {navigation.next ? (
                      <Link
                        to={`/tutorials/${navigation.next.slug}`}
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium group"
                      >
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Next Tutorial</div>
                          <div className="font-medium">{navigation.next.title}</div>
                        </div>
                        <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Back to Tutorials Link */}
            <Link
              to="/tutorials"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to All Tutorials</span>
            </Link>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
}